import React from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { Switch, Route, Redirect, useRouteMatch, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import isUndefined from 'lodash.isundefined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// Icons
import MenuAccountSettings from '../../_atoms/MenuAccountSettings';
import MenuConnections from '../../_atoms/MenuConnections';
import MenuDashboard from '../../_atoms/MenuDashboard';
import MenuEvents from '../../_atoms/MenuEvents';
import MenuMemberDirectory from '../../_atoms/MenuMemberDirectory';
import MenuMessages from '../../_atoms/MenuMessages';
import MenuProperties from '../../_atoms/MenuProperties';
import MenuRetailers from '../../_atoms/MenuRetailers';
import MenuTrustees from '../../_atoms/MenuTrustees';

// Pages
import BillingPaymentHistoryPage from './Billing/BillingPaymentHistoryPage';
import BillingAutoRenewPage from './Billing/BillingAutoRenewPage';
import BillingPaymentPreferencesPage from './Billing/BillingPaymentPreferencesPage';
import CompanyEditPage from './CompanyEdit/CompanyEditPage';
import ContactsSavedPage from './Connections/ContactsSavedPage';
import CompaniesSavedPage from './Connections/CompaniesSavedPage';
import { MyMatchesPage } from './Connections/MyMatchesPage';
import { CreateMatchCriteriaPage } from './Connections/CreateMatchCriteriaPage';
import { UpdateMatchCriteriaPage } from './Connections/UpdateMatchCriteriaPage';
import { MembersLikeMePage } from './Connections/MembersLikeMePage';
import { ShowMatchCriteriaPage } from './Connections/ShowMatchCriteriaPage';
import Dashboard from './Dashboard';
import EventBadgePreviewPage from './Events/EventBadgePreviewPage';
import EventDetailPage from './Events/EventDetailPage';
import EventLandingPage from './Events/EventLandingPage';
import MarketplacesIQLandingPage from './MarketplacesIQ/MarketplacesIQLandingPage';
import MessagesIndexPage from './Messages/MessagesIndexPage';
import EditProfileBasicPage from './Profile/EditProfileBasicPage';
import EditProfileContactPage from './Profile/EditProfileContactPage';
import EditProfileEduPage from './Profile/EditProfileEduPage';
import AccountPrefsPage from './Account/AccountPrefsPage';
import EditPropertyPage from './Properties/EditPropertyPage';
import FindPropertiesPage from './Properties/FindPropertiesPage';
import ListedPropertiesPage from './Properties/ListedPropertiesPage';
import PropertiesSavedPage from './Properties/PropertiesSavedPage';
import ShowPropertyPage from './Properties/ShowPropertyPage';
import RetailersLandingPage from './Retailers/RetailersLandingPage';
import TrusteeEventDetailPage from './Trustees/TrusteeEventDetailPage';
import TrusteesLandingPage from './Trustees/TrusteesLandingPage';

// Module components
import OpenStrengthModalOnHash from './OpenStrengthModalOnHash';
import AccountSettingsSubnav from '../../_molecules/AccountSettingsSubnav';
import EditPropertiesSubnav from '../../_molecules/EditPropertiesSubnav';
import ErrorHandler from '../../_molecules/ErrorHandler';

// Shared components
import { SidebarItem, SidebarItemExternal } from '@molecules/SidebarItems';
import SidebarSubnav from '@molecules/SidebarSubnav';
import FixedInformationButton from '@molecules/FixedInformationButton';
import LoadingArea from '@molecules/LoadingArea';
import SubnavItem from '@molecules/SubnavItem';
import SubnavItemExternal from '@molecules/SubnavItemExternal';
import Sidebar from '@organisms/Sidebar';

// Functionality
import useRestrictedSections from '../../../hooks/useRestrictedSections';
import useMessagesUnread from '../../_organisms/MessagesInbox/hooks/useMessagesUnread';
import { mediaQueries } from '@general/breakpoints';
import useIsViewerActiveMember from '@hooks/useIsViewerActiveMember';
import useOauthUserInfo from '@hooks/useOauthUserInfo';
import useViewer from '@hooks/useViewer';
import useIcscTracking from '@tracking/useIcscTracking';
import { getLoginPath } from '../../../helpers/url.helpers';
import UpdatePasswordPage from './Account/UpdatePasswordPage';
import LoggedInProvider from '../../../context/LoggedInProvider';
import PageContainer from '@molecules/PageContainer';
import PasswordChangeContextProvider from '@context/PasswordChangeProvider';

// show mobile view for phones & tablets
const breakpoint = mediaQueries.lg;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    @media ${breakpoint} {
        flex-direction: row;
    }
`;

const ContainerMain = styled.div`
    width: 100%;
    @media ${breakpoint} {
        width: 75%;
    }
`;

const MyIcscPages = () => {
    const { error, viewer } = useViewer();
    const { userInfo } = useOauthUserInfo();
    const isViewerActiveMember = useIsViewerActiveMember();
    const unreadCount = useMessagesUnread();
    useErrorHandler(error);
    const location = useLocation();
    const isSectionRestricted = useRestrictedSections(location.pathname);
    const match = useRouteMatch();
    const urlSegments = window.location.pathname.split('/').filter(segment => segment.length > 0);
    const feedbackSections = ['events', 'connections', 'properties', 'messages'];

    const { Track } = useIcscTracking({
        app_area: 'My ICSC',
        app_section: urlSegments[1] ? urlSegments[1] : 'dashboard',
    });

    if (!viewer || isUndefined(userInfo)) {
        return <LoadingArea />;
    }

    if (userInfo === null) {
        window.location.href = getLoginPath();
        return null;
    }

    return (
        <ErrorHandler>
            <Track>
                {feedbackSections.includes(urlSegments[1]) && (
                    <FixedInformationButton cookieName="my_icsc_info_sidebar_shown" labelText="Feedback">
                        <p>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </p>

                        <h4 className="bold mb-3">Welcome!</h4>

                        <p className="display8">
                            Welcome to My ICSC. Please provide feedback on the new networking and connections features
                            by filling out a{' '}
                            <a
                                className="text-azure"
                                href="https://www.surveymonkey.com/r/3LWL65T"
                                target="_blank"
                                rel="noreferrer"
                            >
                                quick survey
                            </a>
                            .
                        </p>
                    </FixedInformationButton>
                )}

                <Container>
                    <Sidebar>
                        <SidebarItem
                            to={match.url}
                            label="Dashboard"
                            mobileLabel="My ICSC Dashboard"
                            activeOnlyWhenExact
                            icon={MenuDashboard}
                        />

                        {isViewerActiveMember === false && isSectionRestricted && <Redirect to="/my-icsc" exact />}
                        {isViewerActiveMember && (
                            <>
                                <SidebarItem to={`${match.url}/events/`} label="Events" icon={MenuEvents} />

                                <SidebarItem
                                    to={`${match.url}/connections/`}
                                    label="Connections"
                                    icon={MenuConnections}
                                />

                                <SidebarSubnav
                                    to={`${match.url}/connections/`}
                                    label="Connections"
                                    icon={MenuConnections}
                                >
                                    <SubnavItem exact={false} label="My Matches" to="/my-icsc/connections/matches" />
                                    <SubnavItem label="Members Like Me" to="/my-icsc/connections/members-like-me" />
                                    <SubnavItem
                                        exact={false}
                                        label="Saved Contacts"
                                        to="/my-icsc/connections/saved-contacts"
                                    />

                                    <SubnavItem
                                        exact={false}
                                        label="Saved Companies"
                                        to="/my-icsc/connections/saved-companies"
                                    />
                                </SidebarSubnav>

                                <SidebarItem to={`${match.url}/properties/`} label="Properties" icon={MenuProperties} />
                                <SidebarSubnav to={`${match.url}/properties/`}>
                                    <EditPropertiesSubnav />
                                </SidebarSubnav>

                                <SidebarItem
                                    to={`${match.url}/marketplaces-iq/`}
                                    label="Marketplaces IQ"
                                    icon={MenuProperties}
                                />

                                <SidebarSubnav to={`${match.url}/marketplaces-iq/`}>
                                    <SubnavItemExternal
                                        label="Get Started"
                                        to="https://www.narrpr.com/home?propertymode=commercial&action=maps&cbcode=usicsc-n&ssocode=usicsc-n&utm_source=ICSC-ICSC&utm_medium=deep-link-sso&utm_campaign=Dashboard"
                                        target="_blank"
                                    />

                                    <SubnavItemExternal
                                        label="Learn More"
                                        to="https://www.icsc.com/marketplacesiq"
                                        target="_self"
                                    />
                                    {/* <SubnavItemExternal label="Learn More" to="/marketplaces-iq" target="_self" /> */}
                                </SidebarSubnav>

                                {isViewerActiveMember && (
                                    <>
                                        <SidebarItem
                                            to={`${match.url}/messages/`}
                                            label="Messages"
                                            icon={MenuMessages}
                                            badgeText={unreadCount}
                                        />
                                        <SidebarSubnav
                                            to={`${match.url}/messages/`}
                                            label="Messages"
                                            icon={MenuMessages}
                                            badgeText={unreadCount}
                                        >
                                            <SubnavItem exact={false} label="Inbox" to="/my-icsc/messages/inbox" />
                                        </SidebarSubnav>
                                    </>
                                )}

                                <SidebarItem to={`${match.url}/retailers/`} label="Retailers" icon={MenuRetailers} />

                                {/* {viewer.isTrustee && ( */}
                                {(viewer.company?.recordId === 5094 ||
                                    [95243, 128345, 320103].includes(viewer?.recordId)) && (
                                    <SidebarItem
                                        to={`${match.url}/trustees/`}
                                        label="Trustee Center"
                                        icon={MenuTrustees}
                                    />
                                )}

                                <SidebarItemExternal
                                    to="/search?type=members"
                                    label="Member Directory"
                                    icon={MenuMemberDirectory}
                                />
                            </>
                        )}

                        <SidebarItem to={`${match.url}/account`} label="My Account" icon={MenuAccountSettings} />
                        <SidebarSubnav to={`${match.url}/account`}>
                            <AccountSettingsSubnav viewer={viewer} />
                        </SidebarSubnav>
                    </Sidebar>

                    <ContainerMain className="py-2 px-md-4">
                        {/* Order matters - Dashboard last */}
                        <Switch>
                            <Redirect to="/my-icsc/connections/matches" from="/my-icsc/connections/" exact />

                            {/* Events */}
                            <Route path={`${match.path}/events/badge-preview`}>
                                <EventBadgePreviewPage />
                            </Route>
                            <Route path={`${match.path}/events/:eventSlug/agenda`}>
                                <EventDetailPage subview="agenda" />
                            </Route>

                            {/*
                            <Route path={`${match.path}/events/:eventSlug/availability`}>
                                <EventDetailPage subview="availability" />
                            </Route>
                            */}

                            <Route path={`${match.path}/events/:eventSlug/sessions`}>
                                <EventDetailPage subview="sessions" />
                            </Route>
                            <Route path={`${match.path}/events/:eventSlug`}>
                                <EventDetailPage />
                            </Route>
                            <Route path={`${match.path}/events`}>
                                <EventLandingPage />
                            </Route>

                            {/* Connections */}
                            <Route exact path={`${match.path}/connections/matches`}>
                                <MyMatchesPage />
                            </Route>
                            <Route path={`${match.path}/connections/members-like-me`}>
                                <MembersLikeMePage />
                            </Route>
                            <Route path={`${match.path}/connections/saved-contacts`}>
                                <ContactsSavedPage />
                            </Route>
                            <Route path={`${match.path}/connections/saved-companies`}>
                                <CompaniesSavedPage />
                            </Route>
                            <Route path={`${match.path}/connections/matches/new`}>
                                <CreateMatchCriteriaPage />
                            </Route>
                            <Route exact path={`${match.path}/connections/matches/:id`}>
                                <ShowMatchCriteriaPage />
                            </Route>
                            <Route path={`${match.path}/connections/matches/:id/edit`}>
                                <UpdateMatchCriteriaPage />
                            </Route>

                            {/* Properties */}
                            <Redirect to="/my-icsc/properties/find" from="/my-icsc/properties/" exact />
                            <Route path={`${match.path}/properties/find`}>
                                <FindPropertiesPage />
                            </Route>
                            <Route path={`${match.path}/properties/saved`}>
                                <PropertiesSavedPage />
                            </Route>
                            <Route path={`${match.path}/properties/listed`} exact>
                                <ListedPropertiesPage />
                            </Route>
                            <Route path={`${match.path}/properties/:company/:member/:id`} exact>
                                <ShowPropertyPage />
                            </Route>
                            <Route path={`${match.path}/properties/:id/edit`} exact>
                                <EditPropertyPage />
                            </Route>

                            {/* Marketplaces IQ */}
                            <Route path={`${match.path}/marketplaces-iq`}>
                                <MarketplacesIQLandingPage />
                            </Route>

                            {/* Retailers */}
                            <Route path={`${match.path}/retailers`}>
                                <RetailersLandingPage />
                            </Route>

                            {/* Messages */}
                            <Redirect to="/my-icsc/messages/inbox" from="/my-icsc/messages/" exact />
                            <Route path={`${match.path}/messages/inbox`}>
                                <MessagesIndexPage />
                            </Route>

                            {/* Trustees */}
                            <Route path={`${match.path}/trustees/:eventSlug`}>
                                <TrusteeEventDetailPage />
                            </Route>
                            <Route path={`${match.path}/trustees`}>
                                <TrusteesLandingPage />
                            </Route>

                            {/* Member Profile Edit */}
                            <Redirect
                                to="/my-icsc/account/edit-profile/basic"
                                from="/my-icsc/account/edit-profile/"
                                exact
                            />
                            <Route path={`${match.path}/account/edit-profile/basic`}>
                                <EditProfileBasicPage />
                            </Route>
                            <Route path={`${match.path}/account/edit-profile/contact`}>
                                <EditProfileContactPage />
                            </Route>
                            <Route path={`${match.path}/account/edit-profile/education`}>
                                <EditProfileEduPage />
                            </Route>

                            {/* Company Profile Edit */}
                            <Route path={`${match.path}/account/edit-company`}>
                                <CompanyEditPage />
                            </Route>

                            <Route path={`${match.path}/account/update-password`}>
                                <LoggedInProvider redirectOnUnauthenticated>
                                    <PageContainer>
                                        <PasswordChangeContextProvider>
                                            <UpdatePasswordPage />
                                        </PasswordChangeContextProvider>
                                    </PageContainer>
                                </LoggedInProvider>
                            </Route>

                            {/* Billing Edit */}
                            <Redirect
                                to="/my-icsc/account/edit-billing/payment-history"
                                from="/my-icsc/account/edit-billing/"
                                exact
                            />
                            <Route path={`${match.path}/account/edit-billing/payment-history`}>
                                <BillingPaymentHistoryPage />
                            </Route>
                            <Route path={`${match.path}/account/edit-billing/payment-preferences`}>
                                <BillingPaymentPreferencesPage />
                            </Route>
                            <Route path={`${match.path}/account/edit-billing/auto-renew`}>
                                <BillingAutoRenewPage />
                            </Route>

                            {/* Account Preferences Edit */}
                            <Redirect to="/my-icsc/account/preferences" from="/my-icsc/account" exact />
                            <Route path={`${match.path}/account`}>
                                <AccountPrefsPage />
                            </Route>

                            {/* Dashboard */}
                            <Route path={match.path}>
                                <OpenStrengthModalOnHash>
                                    <Dashboard />
                                </OpenStrengthModalOnHash>
                            </Route>
                        </Switch>
                    </ContainerMain>
                </Container>
            </Track>
        </ErrorHandler>
    );
};

export default MyIcscPages;
